import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, Eye, Link2, Loader2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import CopyLinkButton from './CopyLinkButton';
import {
  ActionMenuItem,
  ActionsDropdownMenu,
} from './ActionsDropdownMenu';
import DeepLinkSeededCampaignsViewModal from './DeepLinkSeededCampaignsViewModal';
import {
  dlBtnGhostClass,
  dlBtnPrimaryClass,
  dlCardClass,
  dlInputClass,
  dlStatCardClass,
  dlTableCellClass,
  dlTableHeadClass,
  dlTableRowClass,
  dlTableWrapClass,
} from './deepLinkUi';
import type { DeepLinkBlogSeedItem, DeepLinkBlogSeedList } from '../../../models/MasterAdmin/deepLink';
import {
  fetchDeepLinkBlogSeedList,
  seedDeepLinkBlogs,
} from '../../../services/MasterAdmin/deepLinkService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

function statusBadge(seeded: boolean) {
  return seeded ? (
    <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
      Done
    </span>
  ) : (
    <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
      Pending
    </span>
  );
}

const DeepLinkBlogSeedPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [data, setData] = useState<DeepLinkBlogSeedList | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [seedingId, setSeedingId] = useState<string | null>(null);
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const [viewItem, setViewItem] = useState<DeepLinkBlogSeedItem | null>(null);

  const load = useCallback(async (q = search) => {
    setLoading(true);
    try {
      const res = await fetchDeepLinkBlogSeedList({ search: q, limit: 200 });
      setData(res);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load blogs'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    void load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSeedOne = async (item: DeepLinkBlogSeedItem) => {
    if (seeding || seedingId) return;
    setSeedingId(item.blogId);
    setOpenMenuKey(null);
    try {
      const res = await seedDeepLinkBlogs([item.blogId]);
      toast.success(
        res.createdCount ? `Seeded blog link for “${item.title}”` : 'Blog already seeded'
      );
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Seed failed'));
    } finally {
      setSeedingId(null);
    }
  };

  const handleSeedAllPending = async () => {
    if (seeding) return;
    if (!(data?.pendingBlogs || 0)) {
      toast.success('All listed blogs are already seeded');
      return;
    }
    setSeeding(true);
    setOpenMenuKey(null);
    try {
      const res = await seedDeepLinkBlogs();
      toast.success(
        `Seeded ${res.createdCount} blog link(s)${
          res.skippedCount ? `, skipped ${res.skippedCount} already seeded` : ''
        }`
      );
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Bulk seed failed'));
    } finally {
      setSeeding(false);
    }
  };

  const busy = seeding || Boolean(seedingId);

  return (
    <div className="w-full space-y-4">
      <div className={`${dlCardClass} p-4`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
              <BookOpen className="h-4 w-4 text-[#128C7E]" />
              Blog deep links
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
              Seed User-app article campaigns. Short links appear after seeding — use View for full
              links and analytics.
            </p>
          </div>
          <button
            type="button"
            className={dlBtnPrimaryClass}
            disabled={busy || !(data?.pendingBlogs || 0)}
            onClick={() => void handleSeedAllPending()}
          >
            {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Seed all pending
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className={dlStatCardClass}>
            <p className="text-xs text-gray-500">Blogs</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{data?.total ?? '—'}</p>
          </div>
          <div className={dlStatCardClass}>
            <p className="text-xs text-gray-500">Pending</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {data?.pendingBlogs ?? '—'}
            </p>
          </div>
          <div className={dlStatCardClass}>
            <p className="text-xs text-gray-500">Done</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{data?.doneBlogs ?? '—'}</p>
          </div>
          <div className={dlStatCardClass}>
            <p className="text-xs text-gray-500">Campaigns</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {data?.seededCampaigns ?? '—'}
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void load(search);
            }}
            placeholder="Search blog title or id…"
            className={`${dlInputClass} max-w-md`}
          />
          <button type="button" className={dlBtnGhostClass} onClick={() => void load(search)}>
            Search
          </button>
          <button
            type="button"
            className={dlBtnGhostClass}
            onClick={() => {
              setSearch('');
              void load('');
            }}
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading blogs…
        </div>
      ) : (
        <div className={`${dlTableWrapClass} w-full`}>
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-[36%]" />
              <col className="w-[12%]" />
              <col className="w-[42%]" />
              <col className="w-[10%]" />
            </colgroup>
            <thead>
              <tr>
                <th className={`${dlTableHeadClass} ${dlTableCellClass}`}>Blog</th>
                <th className={`${dlTableHeadClass} ${dlTableCellClass}`}>Status</th>
                <th className={`${dlTableHeadClass} ${dlTableCellClass}`}>Short link</th>
                <th className={`${dlTableHeadClass} ${dlTableCellClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).length === 0 ? (
                <tr>
                  <td colSpan={4} className={`${dlTableCellClass} text-gray-500`}>
                    No published blogs found
                  </td>
                </tr>
              ) : (
                (data?.items || []).map((item) => {
                  const menuKey = `blog-${item.blogId}`;
                  return (
                    <tr key={item.blogId} className={dlTableRowClass}>
                      <td className={dlTableCellClass}>
                        <p className="truncate font-medium text-gray-900 dark:text-white">
                          {item.title}
                        </p>
                        <p className="truncate font-mono text-[11px] text-gray-400">{item.blogId}</p>
                      </td>
                      <td className={dlTableCellClass}>{statusBadge(item.seeded)}</td>
                      <td className={dlTableCellClass}>
                        {item.campaign?.shortUrl ? (
                          <div className="flex min-w-0 items-center gap-2">
                            <p className="min-w-0 flex-1 truncate font-mono text-[11px] text-[#128C7E] dark:text-[#25D366]">
                              {item.campaign.shortUrl}
                            </p>
                            <CopyLinkButton text={item.campaign.shortUrl} label="Copy" />
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Not seeded yet</span>
                        )}
                      </td>
                      <td className={`${dlTableCellClass} text-right`}>
                        <ActionsDropdownMenu
                          open={openMenuKey === menuKey}
                          onToggle={() =>
                            setOpenMenuKey((prev) => (prev === menuKey ? null : menuKey))
                          }
                          onClose={() => setOpenMenuKey(null)}
                        >
                          {item.campaign ? (
                            <ActionMenuItem
                              icon={<Eye className="h-4 w-4" />}
                              label="View"
                              onClick={() => {
                                setOpenMenuKey(null);
                                setViewItem(item);
                              }}
                            />
                          ) : null}
                          {!item.seeded ? (
                            <ActionMenuItem
                              icon={
                                seedingId === item.blogId ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Link2 className="h-4 w-4" />
                                )
                              }
                              label="Seed blog"
                              disabled={busy}
                              onClick={() => void handleSeedOne(item)}
                            />
                          ) : null}
                          {item.seeded && !item.campaign ? (
                            <ActionMenuItem
                              icon={<Link2 className="h-4 w-4" />}
                              label="Already seeded"
                              disabled
                              onClick={() => setOpenMenuKey(null)}
                            />
                          ) : null}
                        </ActionsDropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <DeepLinkSeededCampaignsViewModal
        open={Boolean(viewItem?.campaign)}
        title={viewItem?.title || 'Blog link'}
        subtitle={viewItem?.blogId}
        entries={
          viewItem?.campaign
            ? [{ label: 'Blog', campaign: viewItem.campaign }]
            : []
        }
        onClose={() => setViewItem(null)}
      />
    </div>
  );
};

export default DeepLinkBlogSeedPanel;
